import { useApolloClient } from "@apollo/client";
import { MenuItem, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { Glossary } from "src/components/Text/Glossary";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { asyncForEach } from "src/services/asyncHelpers";
import { maybePluralize } from "src/services/format";
import { OfferError } from "../errors";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canAcceptOffers } from "../validations/acceptOffers";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

const ACCEPT_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE = 500;

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  onOffersAccepted?: () => void;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Accepting this offer will decline any previously accepted offers from the selected student.",
    "Accepting these offers will decline any previously accepted offers from the selected students."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, accept offer", "Yes, accept offers");

export const AcceptOffer = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Accept offers",
  onOffersAccepted,
  isBulkAction = false,
}: Props) => {
  const client = useApolloClient();
  const queryClient = useQueryClient();

  const { mutateAsync: acceptBulkFormSchoolOffer } = FSQ.useBulkAcceptOffer();

  const toast = useAvelaToast();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async () => {
    const schoolForms: FormSchoolStatus[] = selectedFormSchoolStatuses;
    const count = schoolForms.length;
    const trackArgs = {
      actionType: "ACCEPT_OFFER" as const,
      bulk: isBulkAction,
      count,
      async: false,
    };

    const offerNoun = maybePluralize(count, "offer");

    trackFormAction({ verb: "Clicked", ...trackArgs });

    if (
      await confirm({
        header: <Text>{`Accept ${count} ${offerNoun}?`}</Text>,
        body: (
          <Text>
            <Glossary>{confirmationBodyText(count)}</Glossary>
          </Text>
        ),
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      })
    ) {
      trackFormAction({ verb: "Confirmed", ...trackArgs });

      const loadingToastId = toast({
        title: `Working hard`,
        description: `Accepting ${count} ${offerNoun}...`,
        status: "loading",
        duration: null,
      });

      try {
        const offers = schoolForms.map((result) => {
          if (!result.offers?.[0]?.id) {
            throw new OfferError("One of the selected form(s) has no offer");
          }

          return {
            offer_id: result.offers[0].id,
          };
        });

        await asyncForEach(
          _.chunk(offers, ACCEPT_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE),
          (offerChunk) =>
            acceptBulkFormSchoolOffer({
              body: offerChunk,
            })
        );

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitySummaryByEnrollmentPeriodKey],
            exact: false,
          }),
          client.refetchQueries({
            include: [
              FETCH_OFFER_WAITLIST_STATUS,
              GET_ASSIGNED_FORMS_BY_FORM_ID,
            ],
          }),
        ]);

        if (onRefetch) onRefetch();

        toast.close(loadingToastId);
        toast({
          description: `${count} ${offerNoun} accepted`,
          status: "info",
        });
        if (onOffersAccepted) onOffersAccepted();
        trackFormAction({ verb: "Completed", ...trackArgs });
      } catch (err: any) {
        trackFormActionError(err, trackArgs);
        toast.close(loadingToastId);
        let message = "Check your network and try again.";

        if (err instanceof OfferError) {
          message = err.message;
        }

        toast.error({
          title: "Oops! Something went wrong!",
          description: message,
        });
        console.error(err);
      }
    } else {
      trackFormAction({ verb: "Canceled", ...trackArgs });
    }
  };

  return (
    <WithUserPermissions permissions={["form:update"]}>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canAcceptOffers(selectedFormSchoolStatuses, selectedFormSchools)
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </WithUserPermissions>
  );
};
